import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SelectionDto } from '../api/sbapi/sbapi-api-model';
import {
	selectIsSelectionInBetslip,
	selectVisibility,
	toggleSelection,
	addSelections,
	minimizeBetslip,
	selectItemCount,
	changeShouldApplyBonus,
	selectIsExpanded,
	selectSelectionIds,
	selectPendingSelectionIds,
	toggleSelections
} from '~/components/sportsbook/lobby/features/betslip/ducks';
import { AppDispatch, RootState } from '../../../../store/types';

/**
 * Hook to give direct access to betslip functionality for the rest of the application.
 */
const useSportsbookBetslip = () => {
	const dispatch: AppDispatch = useDispatch();
	const betslipVisibility = useSelector(selectVisibility);
	const betslipItemCount = useSelector(selectItemCount);
	const betslipSelectionIds = useSelector(selectSelectionIds);
	const betslipPendingSelectionIds = useSelector(selectPendingSelectionIds);
	const isBetslipExpanded = useSelector(selectIsExpanded);

	const addSelectionsToBetslip = useCallback(
		(selections: Array<SelectionDto>, tag?: string) => dispatch(addSelections(selections, tag)),
		[dispatch]
	);

	const toggleSelectionCallback = useCallback(
		(selection: SelectionDto, tag: string) => dispatch(toggleSelection(selection, tag)),
		[dispatch]
	);

	const toggleSelectionsCallback = useCallback(
		(selections: SelectionDto[], tag: string) => dispatch(toggleSelections(selections, tag)),
		[dispatch]
	);

	const minimizeBetslipCallback = useCallback(() => dispatch(minimizeBetslip()), [dispatch]);

	const setShouldApplyBonus = useCallback(
		(shouldApplyBonus: boolean) => dispatch(changeShouldApplyBonus(shouldApplyBonus)),
		[dispatch]
	);

	return useMemo(
		() => ({
			betslipVisibility,
			betslipItemCount,
			betslipSelectionIds,
			betslipPendingSelectionIds,
			isBetslipExpanded,
			addSelectionsToBetslip,
			toggleSelection: toggleSelectionCallback,
			toggleSelections: toggleSelectionsCallback,
			minimizeBetslip: minimizeBetslipCallback,
			setShouldApplyBonus
		}),
		[
			betslipVisibility,
			betslipItemCount,
			betslipSelectionIds,
			betslipPendingSelectionIds,
			isBetslipExpanded,
			addSelectionsToBetslip,
			toggleSelectionCallback,
			toggleSelectionsCallback,
			minimizeBetslipCallback,
			setShouldApplyBonus
		]
	);
};

/**
 * Hook implemented specifically for the selection button component.
 */
export const useSportsbookBetslipSelection = (selectionId: string) => {
	const dispatch: AppDispatch = useDispatch();

	const isSelectionInBetslip = useSelector((state: RootState) => selectIsSelectionInBetslip(state, selectionId));

	const toggleSelectionCallback = useCallback(
		(selection: SelectionDto, tag: string) => dispatch(toggleSelection(selection, tag)),
		[dispatch]
	);

	return useMemo(
		() => ({
			isSelectionInBetslip,
			toggleSelection: toggleSelectionCallback
		}),
		[isSelectionInBetslip, toggleSelectionCallback]
	);
};

export default useSportsbookBetslip;
